/**
 * Replaces the duplicated `isUpdatingForm` boolean pattern that lives in
 * almost every enrichment tab today. Tabs flip the flag while patching the
 * form programmatically so their own `valueChanges` subscription does not
 * push a half-built form back to the data service.
 *
 * Usage:
 *
 * ```ts
 * private readonly updating = new FormUpdateGuard();
 *
 * // wrap any programmatic form mutation
 * this.updating.run(() => {
 *   this.form.patchValue(...);
 *   this.array.clear();
 *   ...
 * });
 *
 * // skip valueChanges while a guarded run is in flight
 * this.form.valueChanges.pipe(filter(() => !this.updating.busy))...
 * ```
 *
 * The guard is reentrant-safe — nested `run()` calls share the same flag and
 * only the outermost call clears it. This matches what tabs do today (e.g.
 * `populatePopSections` is called from inside the parent's `run()`).
 */
export class FormUpdateGuard {
  private depth = 0;

  /** True while at least one `run()` is in progress on the call stack. */
  get busy(): boolean {
    return this.depth > 0;
  }

  /** Run a synchronous mutation with the guard set. Returns the value `fn`
   *  returned. Always clears the flag, even if `fn` throws. */
  run<T>(fn: () => T): T {
    this.depth += 1;
    try {
      return fn();
    } finally {
      this.depth -= 1;
    }
  }
}
